/**
 * Normalize API values that mean on-duty / checked in for delivery partner.
 * Handles nested shapes and non-boolean encodings without backend changes.
 */
function coerceAvailability(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no') return false;
  }
  return null;
}

/**
 * Read partner on-duty / checked-in flag.
 *
 * GET /delivery/profile/ returns `is_available` on the **root** of `data` (not under `partner`).
 * We must read that **before** legacy `partner.is_available` so login payloads that only
 * nested availability under `partner` do not override the server profile.
 */
export function readPartnerIsAvailableFromPayload(data: unknown): boolean | null {
  if (data == null || typeof data !== 'object') return null;

  const o = data as Record<string, unknown>;

  // Unwrap { success, data: { ... } } first
  if (o.data != null && typeof o.data === 'object' && !Array.isArray(o.data)) {
    const inner = readPartnerIsAvailableFromPayload(o.data);
    if (inner !== null) return inner;
  }

  // Authoritative for delivery profile API
  const root = coerceAvailability(o.is_available);
  if (root !== null) return root;

  const fallback: unknown[] = [
    (o.partner as Record<string, unknown> | undefined)?.is_available,
    (o.user as Record<string, unknown> | undefined)?.partner &&
      ((o.user as Record<string, unknown>).partner as Record<string, unknown>)?.is_available,
    o.is_on_duty,
    o.on_duty,
    (o.delivery_partner as Record<string, unknown> | undefined)?.is_available,
    (o.profile as Record<string, unknown> | undefined)?.partner &&
      ((o.profile as Record<string, unknown>).partner as Record<string, unknown>)?.is_available,
  ];

  for (const v of fallback) {
    const c = coerceAvailability(v);
    if (c !== null) return c;
  }

  return null;
}

/**
 * Read the server-derived "checked in today" flag from a profile payload.
 *
 * GET /delivery/profile/ returns `is_checked_in_today` which is computed from
 * **today's** Attendance row on the backend (checkin_time set, checkout_time null).
 * This is day-aware and is the right signal for the Check-in/Check-out button —
 * unlike `is_available`, which can be stale across calendar days.
 *
 * Returns `null` if the field is absent (older server payload) so callers can
 * fall back to the legacy `is_available`-based resolver.
 */
export function readPartnerIsCheckedInTodayFromPayload(
  data: unknown
): boolean | null {
  if (data == null || typeof data !== 'object') return null;

  const o = data as Record<string, unknown>;

  if (o.data != null && typeof o.data === 'object' && !Array.isArray(o.data)) {
    const inner = readPartnerIsCheckedInTodayFromPayload(o.data);
    if (inner !== null) return inner;
  }

  const candidates: unknown[] = [
    o.is_checked_in_today,
    (o.partner as Record<string, unknown> | undefined)?.is_checked_in_today,
    (o.delivery_partner as Record<string, unknown> | undefined)
      ?.is_checked_in_today,
    (o.profile as Record<string, unknown> | undefined)?.is_checked_in_today,
  ];

  for (const v of candidates) {
    const c = coerceAvailability(v);
    if (c !== null) return c;
  }

  return null;
}
